import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, Input, InputNumber, Select, Space, Switch, Table, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import userSettingsController from '@shared/controllers/UserSettingsController';
import { useDebouncedCallback } from '@shared/hooks/useDebouncedCallback';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Универсальный компонент для управления локальными настройками
 * Доступен только в development среде (localhost, kampus.local)
 * Может использоваться как в админ панели, так и в пользовательской части
 */
const LocalSettingsTab = ({
  showTitle = true,
  titleLevel = 3,
  translationNamespace = ['localSettings'],
  containerStyle = {},
  tableProps = {},
}) => {
  const { t } = useTranslation(translationNamespace);
  const [settings, setSettings] = useState(userSettingsController.getSettings());
  const [loading, setLoading] = useState(false);

  // Описания настроек для таблицы
  const settingsDescriptions = {
    // Настройки уведомлений
    hideInitNotify: {
      name: 'Скрывать начальные уведомления',
      nameKey: 'hideInitNotify.name',
      description: 'Отключает показ первичных уведомлений при входе в систему',
      descriptionKey: 'hideInitNotify.description',
      type: 'boolean',
    },
    maxNotificationShows: {
      name: 'Максимальное количество показов уведомления',
      nameKey: 'maxNotificationShows.name',
      description: 'Количество раз, которое одно уведомление может быть показано',
      descriptionKey: 'maxNotificationShows.description',
      type: 'number',
      min: 1,
      max: 10,
    },
    autoHideNotifications: {
      name: 'Автоматически скрывать уведомления',
      nameKey: 'autoHideNotifications.name',
      description: 'Автоматическое скрытие уведомлений через определенное время',
      descriptionKey: 'autoHideNotifications.description',
      type: 'boolean',
    },
    notificationSound: {
      name: 'Звук уведомлений',
      nameKey: 'notificationSound.name',
      description: 'Включает звуковое сопровождение уведомлений',
      descriptionKey: 'notificationSound.description',
      type: 'boolean',
    },

    // Настройки темы и интерфейса
    isBlackTheme: {
      name: 'Темная тема',
      nameKey: 'isBlackTheme.name',
      description: 'Переключение между светлой и темной темой интерфейса',
      descriptionKey: 'isBlackTheme.description',
      type: 'boolean',
    },
    showNotificationTime: {
      name: 'Показывать время уведомлений',
      nameKey: 'showNotificationTime.name',
      description: 'Отображение времени получения уведомления',
      descriptionKey: 'showNotificationTime.description',
      type: 'boolean',
    },

    // Настройки локализации
    language: {
      name: 'Язык интерфейса',
      nameKey: 'language.name',
      description: 'Выбор языка пользовательского интерфейса',
      descriptionKey: 'language.description',
      type: 'select',
      options: [
        { value: 'ru', label: 'Русский' },
        { value: 'en', label: 'English' },
      ],
    },
    timezone: {
      name: 'Часовой пояс',
      nameKey: 'timezone.name',
      description: 'Часовой пояс для отображения времени',
      descriptionKey: 'timezone.description',
      type: 'select',
      options: [
        { value: 'Europe/Moscow', label: 'Europe/Moscow' },
        { value: 'UTC', label: 'UTC' },
      ],
    },
    dateFormat: {
      name: 'Формат даты',
      nameKey: 'dateFormat.name',
      description: 'Формат отображения дат в интерфейсе',
      descriptionKey: 'dateFormat.description',
      type: 'select',
      options: [
        { value: 'DD.MM.YYYY', label: 'ДД.ММ.ГГГГ' },
        { value: 'MM/DD/YYYY', label: 'ММ/ДД/ГГГГ' },
        { value: 'YYYY-MM-DD', label: 'ГГГГ-ММ-ДД' },
      ],
    },
    timeFormat: {
      name: 'Формат времени',
      nameKey: 'timeFormat.name',
      description: 'Формат отображения времени (12 или 24 часа)',
      descriptionKey: 'timeFormat.description',
      type: 'select',
      options: [
        { value: '24h', label: '24 часа' },
        { value: '12h', label: '12 часов' },
      ],
    },
  };

  // Debounced функция для сохранения настроек
  const debouncedSaveSetting = useDebouncedCallback((key, value) => {
    setLoading(true);
    try {
      const success = userSettingsController.setSetting(key, value);
      if (success) {
        setSettings(userSettingsController.getSettings());
      }
    } catch (error) {
      console.error('Error saving setting:', error);
    } finally {
      setLoading(false);
    }
  }, 300);

  // Обработчик изменения настройки
  const handleSettingChange = (key, value) => {
    // Обновляем локальное состояние немедленно для UI
    setSettings(prev => ({ ...prev, [key]: value }));
    // Сохраняем с debounce
    debouncedSaveSetting(key, value);
  };

  // Подписка на изменения настроек
  useEffect(() => {
    const unsubscribe = userSettingsController.onSettingsChange(event => {
      setSettings(event.allSettings);
    });

    return unsubscribe;
  }, []);

  // Генерация данных для таблицы
  const tableData = useMemo(() => {
    return Object.entries(settingsDescriptions).map(([key, config]) => ({
      key,
      name: config.name,
      nameKey: config.nameKey,
      description: config.description,
      descriptionKey: config.descriptionKey,
      value: settings[key],
      type: config.type,
      options: config.options,
      min: config.min,
      max: config.max,
    }));
  }, [settings]);

  // Функция для получения переведенного текста с fallback
  const getTranslatedText = (key, fallback) => {
    try {
      const translated = t(key);
      return translated && translated !== key ? translated : fallback;
    } catch {
      return fallback;
    }
  };

  // Рендер ячейки со значением
  const renderValue = record => {
    const { key, value, type, options, min, max } = record;

    switch (type) {
      case 'boolean':
        return <Switch checked={value} onChange={checked => handleSettingChange(key, checked)} loading={loading} />;

      case 'number':
        return (
          <InputNumber
            value={value}
            min={min}
            max={max}
            onChange={newValue => handleSettingChange(key, newValue)}
            style={{ width: '100%' }}
            disabled={loading}
          />
        );

      case 'select':
        return (
          <Select
            value={value}
            onChange={newValue => handleSettingChange(key, newValue)}
            style={{ width: '100%' }}
            loading={loading}
          >
            {options.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        );

      default:
        return <Input value={value} onChange={e => handleSettingChange(key, e.target.value)} disabled={loading} />;
    }
  };

  // Колонки таблицы
  const columns = [
    {
      title: getTranslatedText('settingName', 'Название настройки'),
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (text, record) => (
        <Space>
          <Text strong>{getTranslatedText(record.nameKey, record.name)}</Text>
          <Tooltip title={getTranslatedText(record.descriptionKey, record.description)}>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: getTranslatedText('value', 'Значение'),
      dataIndex: 'value',
      key: 'value',
      width: '25%',
      render: (_, record) => renderValue(record),
    },
    {
      title: getTranslatedText('settingDescription', 'Описание'),
      dataIndex: 'description',
      key: 'description',
      width: '45%',
      render: (text, record) => (
        <Text type='secondary'>{getTranslatedText(record.descriptionKey, record.description)}</Text>
      ),
    },
  ];

  // Проверяем, что мы в development среде
  if (!userSettingsController.isDevelopmentEnvironment()) {
    return null;
  }

  return (
    <div style={{ padding: '20px 0', ...containerStyle }}>
      <Space direction='vertical' style={{ width: '100%' }} size='large'>
        {showTitle && (
          <div>
            <Title level={titleLevel}>{getTranslatedText('title', 'Локальные настройки')}</Title>
            <Alert
              message={getTranslatedText('developmentMode', 'Режим разработки')}
              description={getTranslatedText(
                'devDescription',
                'Эти настройки доступны только в среде разработки (localhost, kampus.local) и позволяют настраивать поведение системы для локального тестирования.'
              )}
              type='info'
              showIcon
              style={{ marginBottom: 20 }}
            />
          </div>
        )}

        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          loading={loading}
          size='middle'
          bordered
          rowKey='key'
          scroll={{ x: 800 }}
          {...tableProps}
        />

        <div style={{ marginTop: 20 }}>
          <Text type='secondary'>
            {getTranslatedText('autoSaveNote', '* Изменения сохраняются автоматически с задержкой 300мс после ввода')}
          </Text>
        </div>
      </Space>
    </div>
  );
};

export default LocalSettingsTab;
